/**
 * The per-tab consent cache: one entry, owned by one fan, with a TTL.
 *
 * **The fan is part of the key, and that is the whole design.**
 * docs/subsystems/loyalty-toasts.md records the account-switch trap and its fix -- the snapshot, the
 * fan fingerprint and the feed mark have to be cleared TOGETHER, through one `forget()`, or a new
 * fan's first award vanishes into the re-baseline. That fix works, and it is a guard, which means it
 * has an ordering: a caller can run it too late or not at all. `toasts/gate.js`'s own
 * `urc.loyalty.toastEnrolled` was the proof -- it was read from `whenToastEligible()` before the
 * guard that would have cleared it ever ran, so an account switch kept answering `armed`. It was
 * fixed in parallel with this file and by the same move: `toasts/enrolled.js` now puts the token's
 * `sub` in the entry, so the two caches share one shape rather than one of them being the warning.
 *
 * So this does not guard. It records WHOSE value it holds and refuses to answer for anybody else,
 * which makes an account switch a cache **miss** rather than a sequence somebody has to get right.
 *
 * One entry rather than one per fan: only one fan is signed in per tab at a time, so a second entry
 * could only ever be a previous fan's, and keeping it would be keeping exactly the thing this
 * module exists to drop.
 *
 * **Nothing is imported here.** Every input is an argument, so store.test.js drives the whole thing
 * under `node --test` with no `window`, no `sessionStorage` and no clock -- the same split
 * ./machine.js and ../urcAuth.js follow, and the reason all three have tests where authSignal.js,
 * which reached for `document` and `window` directly, never did.
 */

/**
 * Where the entry lives. Named like the other loyalty keys (`urc.loyalty.*`) so the whole set is
 * visible together in devtools.
 */
export const STORAGE_KEY = 'urc.loyalty.consent';

/**
 * Where the machine's back-off after a failed read lives. Beside the answer and in the same storage,
 * because this site is a page load per navigation: a cooldown held only in memory was gone by the
 * next click, and the next page asked again -- which is the whole cost it exists to stop.
 */
export const COOLDOWN_KEY = 'urc.loyalty.consent.cooldown';

/**
 * Build a store over injected storage and clock.
 *
 * @param {Object}       options         Inputs.
 * @param {Object|null}  options.storage A `sessionStorage`-shaped object, or null for none.
 * @param {() => number} options.now     Epoch milliseconds.
 * @param {number}       options.ttlMs   How long an entry stays usable.
 * @return {Object} The store: `read`, `write`, `forget`, and the cooldown's four.
 */
export function createConsentStore( { storage, now, ttlMs } ) {
	/**
	 * The cooldown, held in memory as well as in storage for the same reason as the entry.
	 *
	 * @type {{sub: string, until: number, failures: number, reason: string}|null}
	 */
	let cooldownMemo = null;

	/**
	 * The entry, held in memory as well as in storage.
	 *
	 * Not an optimisation: private-browsing modes and disabled site data make every storage call
	 * throw, and this is what keeps the cache working for the life of the page in that case rather
	 * than degrading to no cache at all.
	 */
	let memo = null;

	/**
	 * Read the raw entry from storage, or null.
	 *
	 * Every failure answers null: a storage that throws, an absent key, malformed JSON, an entry
	 * whose shape is not what this version writes. A cache is never worth an exception.
	 *
	 * @return {{sub: string, granted: boolean, at: number}|null} The entry.
	 */
	function stored() {
		if ( ! storage ) {
			return null;
		}

		try {
			const raw = storage.getItem( STORAGE_KEY );

			if ( typeof raw !== 'string' || raw === '' ) {
				return null;
			}

			const parsed = JSON.parse( raw );

			if ( ! parsed || typeof parsed !== 'object' ) {
				return null;
			}

			if ( typeof parsed.sub !== 'string' || typeof parsed.granted !== 'boolean' ) {
				return null;
			}

			return { sub: parsed.sub, granted: parsed.granted, at: Number( parsed.at ) || 0 };
		} catch ( error ) {
			return null;
		}
	}

	/**
	 * The cached value for `sub`, or null when there is nothing usable for that fan.
	 *
	 * Three ways to miss, and they are all the same answer: no entry, an entry belonging to somebody
	 * else, or an entry older than the TTL.
	 *
	 * @param {string} sub The fan's Auth0 `sub`.
	 * @return {{granted: boolean, checkedAt: number}|null} The value and when it was confirmed.
	 */
	function read( sub ) {
		// An empty `sub` is "there is no fan", which must never read or write. Returning null here
		// rather than at each call site is what makes that impossible to forget.
		if ( ! sub ) {
			return null;
		}

		const entry = memo && memo.sub === sub ? memo : stored();

		if ( ! entry || entry.sub !== sub ) {
			return null;
		}

		if ( now() - entry.at >= ttlMs ) {
			return null;
		}

		memo = entry;

		return { granted: entry.granted, checkedAt: entry.at };
	}

	/**
	 * Record a value the server confirmed.
	 *
	 * Only ever called with a server's answer -- never with what a caller asked for. A cache that
	 * can hold an optimistic value is a cache that can disagree with Auth0, and on a consent record
	 * that disagreement is the bug rather than a stale read.
	 *
	 * @param {string}  sub     The fan's Auth0 `sub`.
	 * @param {boolean} granted The confirmed value.
	 * @return {void}
	 */
	function write( sub, granted ) {
		if ( ! sub ) {
			return;
		}

		const entry = { sub, granted: Boolean( granted ), at: now() };

		memo = entry;

		if ( ! storage ) {
			return;
		}

		try {
			storage.setItem( STORAGE_KEY, JSON.stringify( entry ) );
		} catch ( error ) {
			// Quota, private mode, or site data switched off. The memo above still serves this page.
		}
	}

	/**
	 * Drop everything. Called on a sign-out and whenever the credential stops naming this fan.
	 *
	 * @return {void}
	 */
	function forget() {
		memo = null;

		if ( ! storage ) {
			return;
		}

		try {
			storage.removeItem( STORAGE_KEY );
		} catch ( error ) {
			// Nothing to do: the memo is already gone, which is what this page reads.
		}
	}

	/**
	 * The back-off for this fan, or null when there is none or it has passed.
	 *
	 * Keyed by `sub` like the entry, so a different fan is never held back by somebody else's
	 * failure. `maxMs` is how far ahead a cooldown may legitimately end; one further out than that
	 * was written against a clock that has since moved back, and is ignored rather than honoured
	 * for ever.
	 *
	 * @param {string} sub   The fan's Auth0 `sub`.
	 * @param {number} maxMs The longest a cooldown can run.
	 * @return {{until: number, failures: number, reason: string}|null} The cooldown.
	 */
	function readCooldown( sub, maxMs ) {
		if ( ! sub ) {
			return null;
		}

		let entry = cooldownMemo && cooldownMemo.sub === sub ? cooldownMemo : null;

		if ( ! entry && storage ) {
			try {
				const parsed = JSON.parse( storage.getItem( COOLDOWN_KEY ) || 'null' );

				if (
					parsed &&
					typeof parsed === 'object' &&
					parsed.sub === sub &&
					Number.isFinite( parsed.until ) &&
					Number.isFinite( parsed.failures )
				) {
					entry = {
						sub,
						until: parsed.until,
						failures: parsed.failures,
						reason: typeof parsed.reason === 'string' ? parsed.reason : '',
					};
				}
			} catch ( error ) {
				entry = null;
			}
		}

		if ( ! entry ) {
			return null;
		}

		const left = entry.until - now();

		if ( left <= 0 || left > maxMs ) {
			return null;
		}

		cooldownMemo = entry;

		return { until: entry.until, failures: entry.failures, reason: entry.reason };
	}

	/**
	 * How many failures in a row this fan has had, whether or not the cooldown has passed. The
	 * ladder climbs on consecutive failures, so a lapsed cooldown still counts until an answer.
	 *
	 * @param {string} sub The fan's Auth0 `sub`.
	 * @return {number} The count, 0 when none is recorded.
	 */
	function failuresFor( sub ) {
		if ( cooldownMemo && cooldownMemo.sub === sub ) {
			return cooldownMemo.failures;
		}

		if ( ! storage ) {
			return 0;
		}

		try {
			const parsed = JSON.parse( storage.getItem( COOLDOWN_KEY ) || 'null' );

			return parsed && parsed.sub === sub && Number.isFinite( parsed.failures )
				? parsed.failures
				: 0;
		} catch ( error ) {
			return 0;
		}
	}

	/**
	 * @param {string} sub      The fan's Auth0 `sub`.
	 * @param {number} until    Epoch milliseconds.
	 * @param {number} failures Failures in a row, including this one.
	 * @param {string} reason   What the failure published, so a later page can publish it too.
	 * @return {void}
	 */
	function writeCooldown( sub, until, failures, reason ) {
		if ( ! sub ) {
			return;
		}

		cooldownMemo = { sub, until, failures, reason };

		if ( ! storage ) {
			return;
		}

		try {
			storage.setItem( COOLDOWN_KEY, JSON.stringify( cooldownMemo ) );
		} catch ( error ) {
			// The memo still holds it for this page.
		}
	}

	/** @return {void} */
	function clearCooldown() {
		cooldownMemo = null;

		if ( ! storage ) {
			return;
		}

		try {
			storage.removeItem( COOLDOWN_KEY );
		} catch ( error ) {
			// The memo is gone, which is what this page reads.
		}
	}

	return { read, write, forget, readCooldown, failuresFor, writeCooldown, clearCooldown };
}
